import { gql } from '@apollo/client';

/**
 * 通用配置 GraphQL 类型定义
 */
export interface CommonConfigType {
    id: string;
    configKey: string;
    configValue: string;
    description?: string;
    configType: string;
    isActive: boolean;
    sortOrder: number;
    matchRule: string;
    createdBy?: number;
    updatedBy?: number;
    createdAt?: number;  // unix timestamp 秒数
    updatedAt?: number;  // unix timestamp 秒数
}

/**
 * 带操作人信息的通用配置类型
 */
export interface CommonConfigWithOperatorType {
    id: string;
    configKey: string;
    configValue: string;
    description?: string;
    configType: string;
    isActive: boolean;
    sortOrder: number;
    matchRule: string;
    updatedBy?: number;
    updatedByName?: string;
    createdAt?: number;
    updatedAt?: number;
}

/**
 * 分页请求参数
 */
export interface CommonConfigPageRequest {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDirection?: string;
}

/**
 * 筛选条件
 */
export interface CommonConfigFilters {
    configKey?: string;
    description?: string;
    configType?: string;
    isActive?: boolean;
}

/**
 * 分页响应类型
 */
export interface CommonConfigPageResponse {
    content: CommonConfigWithOperatorType[];
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
}

/**
 * 创建配置输入类型
 */
export interface CreateCommonConfigInput {
    configKey: string;
    configValue?: string;
    description?: string;
    configType?: string;
    isActive?: boolean;
    sortOrder?: number;
    matchRule?: string;
}

/**
 * 更新配置输入类型
 */
export interface UpdateCommonConfigInput {
    configValue?: string;
    description?: string;
    configType?: string;
    isActive?: boolean;
    sortOrder?: number;
    matchRule?: string;
}

/**
 * 通用配置批量操作结果类型
 */
export interface CommonConfigBatchOperationResult {
    successCount: number;
    failureCount: number;
    totalCount: number;
    message: string;
}

/**
 * 查询响应类型
 */
export interface GetAllCommonConfigsResponse {
    getAllCommonConfigs: CommonConfigType[];
}

export interface GetAllCommonConfigsWithPaginationResponse {
    getAllCommonConfigsWithPagination: CommonConfigPageResponse;
}

export interface GetCommonConfigByIdResponse {
    getCommonConfigById: CommonConfigType | null;
}

export interface CreateCommonConfigResponse {
    createCommonConfig: CommonConfigType;
}

export interface UpdateCommonConfigResponse {
    updateCommonConfig: CommonConfigType | null;
}

export interface DeleteCommonConfigResponse {
    deleteCommonConfig: boolean;
}

export interface BatchDeleteCommonConfigsResponse {
    batchDeleteCommonConfigs: CommonConfigBatchOperationResult;
}

export interface BatchUpdateCommonConfigStatusResponse {
    batchUpdateCommonConfigStatus: CommonConfigBatchOperationResult;
}

/**
 * 查询所有配置
 */
export const GET_ALL_COMMON_CONFIGS = gql`
    query GetAllCommonConfigs {
        getAllCommonConfigs {
            id
            configKey
            configValue
            description
            configType
            isActive
            sortOrder
            matchRule
            createdBy
            updatedBy
            createdAt
            updatedAt
        }
    }
`;

/**
 * 根据ID查询配置
 */
export const GET_COMMON_CONFIG_BY_ID = gql`
    query GetCommonConfigById($id: ID!) {
        getCommonConfigById(id: $id) {
            id
            configKey
            configValue
            description
            configType
            isActive
            sortOrder
            matchRule
            createdBy
            updatedBy
            createdAt
            updatedAt
        }
    }
`;

/**
 * 根据configKey查询配置
 */
export interface GetCommonConfigByKeyResponse {
    getCommonConfigByKey: CommonConfigType | null;
}

export interface GetCommonConfigByKeyVariables {
    configKey: string;
}

export const GET_COMMON_CONFIG_BY_KEY = gql`
    query GetCommonConfigByKey($configKey: String!) {
        getCommonConfigByKey(configKey: $configKey) {
            id
            configKey
            configValue
            description
            configType
            isActive
            sortOrder
            matchRule
            createdBy
            updatedBy
            createdAt
            updatedAt
        }
    }
`;

/**
 * 创建配置
 */
export const CREATE_COMMON_CONFIG = gql`
    mutation CreateCommonConfig($input: CreateCommonConfigInput!) {
        createCommonConfig(input: $input) {
            id
            configKey
            configValue
            description
            configType
            isActive
            sortOrder
            matchRule
            createdAt
            updatedAt
        }
    }
`;

/**
 * 更新配置
 */
export const UPDATE_COMMON_CONFIG = gql`
    mutation UpdateCommonConfig($id: ID!, $input: UpdateCommonConfigInput!) {
        updateCommonConfig(id: $id, input: $input) {
            id
            configKey
            configValue
            description
            configType
            isActive
            sortOrder
            matchRule
            updatedAt
        }
    }
`;

/**
 * 删除配置
 */
export const DELETE_COMMON_CONFIG = gql`
    mutation DeleteCommonConfig($id: ID!) {
        deleteCommonConfig(id: $id)
    }
`;

/**
 * 批量删除配置
 */
export const BATCH_DELETE_COMMON_CONFIGS = gql`
    mutation BatchDeleteCommonConfigs($ids: [ID!]!) {
        batchDeleteCommonConfigs(ids: $ids) {
            successCount
            failureCount
            totalCount
            message
        }
    }
`;

/**
 * 批量启用/禁用配置
 */
export const BATCH_UPDATE_COMMON_CONFIG_STATUS = gql`
    mutation BatchUpdateCommonConfigStatus($ids: [ID!]!, $isActive: Boolean!) {
        batchUpdateCommonConfigStatus(ids: $ids, isActive: $isActive) {
            successCount
            failureCount
            totalCount
            message
        }
    }
`;

/**
 * 按前缀建议 configKey（用于搜索框 AutoComplete）
 */
export interface SuggestCommonConfigKeysResponse {
    suggestCommonConfigKeys: string[];
}

export interface SuggestCommonConfigKeysVariables {
    prefix: string;
    limit: number;
}

export const SUGGEST_COMMON_CONFIG_KEYS = gql`
    query SuggestCommonConfigKeys($prefix: String!, $limit: Int!) {
        suggestCommonConfigKeys(prefix: $prefix, limit: $limit)
    }
`;

/**
 * 新增/编辑前的重复校验：查找 (configKey, sortOrder, matchRule) 完全相同的现存配置
 */
export interface FindCommonConfigDuplicatesResponse {
    findCommonConfigDuplicates: CommonConfigType[];
}

export interface FindCommonConfigDuplicatesVariables {
    configKey: string;
    sortOrder?: number | null;
    excludeId?: string | null;
}

export const FIND_COMMON_CONFIG_DUPLICATES = gql`
    query FindCommonConfigDuplicates(
        $configKey: String!
        $sortOrder: Int
        $excludeId: ID
    ) {
        findCommonConfigDuplicates(
            configKey: $configKey
            sortOrder: $sortOrder
            excludeId: $excludeId
        ) {
            id
            configKey
            configValue
            description
            configType
            isActive
            sortOrder
            matchRule
            updatedBy
            updatedAt
        }
    }
`;

/**
 * SpEL matchRule 调试结果
 * - valid=false 表示语法错误
 * - valid=true 时 matched 为求值结果；errorMsg 非空时表示求值异常
 */
export interface CommonConfigDebugRuleResult {
    valid: boolean;
    matched: boolean;
    errorMsg?: string | null;
}

export interface DebugCommonConfigMatchRuleResponse {
    debugCommonConfigMatchRule: CommonConfigDebugRuleResult;
}

export interface DebugCommonConfigMatchRuleVariables {
    expression: string;
    head: Array<{ key: string; value?: string | null }>;
}

export const DEBUG_COMMON_CONFIG_MATCH_RULE = gql`
    query DebugCommonConfigMatchRule($expression: String!, $head: [HeadEntryInput!]!) {
        debugCommonConfigMatchRule(expression: $expression, head: $head) {
            valid
            matched
            errorMsg
        }
    }
`;

/**
 * 列表页「调试配置」弹窗：按 configKey 精确返回该 key 下所有未删除行（含 inactive），sortOrder ASC, id ASC
 */
export interface ListCommonConfigsByConfigKeyResponse {
    listCommonConfigsByConfigKey: CommonConfigType[];
}

export interface ListCommonConfigsByConfigKeyVariables {
    configKey: string;
}

export const LIST_COMMON_CONFIGS_BY_CONFIG_KEY = gql`
    query ListCommonConfigsByConfigKey($configKey: String!) {
        listCommonConfigsByConfigKey(configKey: $configKey) {
            id
            configKey
            configValue
            description
            configType
            isActive
            sortOrder
            matchRule
            createdBy
            updatedBy
            createdAt
            updatedAt
        }
    }
`;

/**
 * 批量按 configKey + head 匹配。用于调试配置弹窗的「调试」按钮
 */
export interface MatchCommonConfigEntry {
    key: string;
    value: {
        matched: boolean;
        config: CommonConfigType | null;
    };
}

export interface MatchCommonConfigsResponse {
    matchCommonConfigs: MatchCommonConfigEntry[];
}

export interface MatchCommonConfigsVariables {
    configKeys: string[];
    head: Array<{ key: string; value?: string | null }>;
}

export const MATCH_COMMON_CONFIGS = gql`
    query MatchCommonConfigs($configKeys: [String!]!, $head: [HeadEntryInput!]!) {
        matchCommonConfigs(configKeys: $configKeys, head: $head) {
            key
            value {
                matched
                config {
                    id
                    configKey
                    configValue
                    description
                    configType
                    isActive
                    sortOrder
                    matchRule
                    createdBy
                    updatedBy
                    createdAt
                    updatedAt
                }
            }
        }
    }
`;

/**
 * 单 configKey 行数与上限（用于新增前的「上限」校验）
 */
export interface CommonConfigCountType {
    currentCount: number;
    limit: number;
}

export interface GetCommonConfigCountByKeyResponse {
    getCommonConfigCountByKey: CommonConfigCountType;
}

export interface GetCommonConfigCountByKeyVariables {
    configKey: string;
}

export const GET_COMMON_CONFIG_COUNT_BY_KEY = gql`
    query GetCommonConfigCountByKey($configKey: String!) {
        getCommonConfigCountByKey(configKey: $configKey) {
            currentCount
            limit
        }
    }
`;

/**
 * 分页查询所有配置（包含操作人信息）
 */
export const GET_ALL_COMMON_CONFIGS_WITH_PAGINATION = gql`
    query GetAllCommonConfigsWithPagination(
        $pageRequest: CommonConfigPageRequestInput
        $filters: CommonConfigFiltersInput
    ) {
        getAllCommonConfigsWithPagination(pageRequest: $pageRequest, filters: $filters) {
            content {
                id
                configKey
                configValue
                description
                configType
                isActive
                sortOrder
                matchRule
                updatedBy
                updatedByName
                createdAt
                updatedAt
            }
            page
            size
            totalElements
            totalPages
            hasNext
            hasPrevious
        }
    }
`;
